import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/Button';
import { ParticleBackground } from '../components/ParticleBackground';
import { User, Lock, CheckCircle } from 'lucide-react';
import styles from './AuthView.module.css';

export default function RegisterView() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const { register, error, clearError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setValidationError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setValidationError('Las contraseñas no coinciden');
      return;
    }

    // Validate password length
    if (password.length < 8) {
      setValidationError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setIsSubmitting(true);

    try {
      // Backend still uses email, but we show it as username
      await register(username, password);
      navigate('/');
    } catch (error) {
      // Error is handled by AuthContext
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = validationError || error;

  return (
    <>
      <ParticleBackground />
      <div className={styles.container}>
        <div className={styles.glassCard}>
          {/* Left Side - Form */}
          <div className={styles.formSection}>
            <div className={styles.logo}>
              <div className={styles.logoIcon}>TM</div>
              <span className={styles.logoText}>TechMahindra</span>
            </div>

            <div className={styles.titleGroup}>
              <h1 className={styles.title}>Crear Cuenta</h1>
              <p className={styles.subtitle}>Regístrate para acceder al sistema</p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              {displayError && (
                <div className={styles.error}>
                  {displayError}
                </div>
              )}

              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="username">Usuario</label>
                <div className={styles.inputWrapper}>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className={styles.input}
                    placeholder="Usuario"
                    autoComplete="username"
                  />
                  <User className={styles.inputIcon} size={20} />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="password">Contraseña</label>
                <div className={styles.inputWrapper}>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={styles.input}
                    placeholder="Contraseña (mín. 8 caracteres)"
                    autoComplete="new-password"
                    minLength={8}
                  />
                  <Lock className={styles.inputIcon} size={20} />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="confirmPassword">Confirmar Contraseña</label>
                <div className={styles.inputWrapper}>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={styles.input}
                    placeholder="Confirmar Contraseña"
                    autoComplete="new-password"
                    minLength={8}
                  />
                  <CheckCircle className={styles.inputIcon} size={20} />
                </div>
              </div>

              <Button
                type="submit"
                className={styles.button}
                disabled={isSubmitting}
                fullWidth
              >
                {isSubmitting ? 'Creando cuenta...' : 'Crear Cuenta'}
              </Button>
            </form>

            <p className={styles.footer}>
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className={styles.link}>
                Inicia sesión aquí
              </Link>
            </p>
          </div>

          {/* Right Side - Visual */}
          <div className={styles.visualSection}>
            <div className={styles.visualOverlay}>
              <div className={styles.visualText}>
                <h2>Calculadora CTC</h2>
                <p>Únete a la herramienta líder en proyección de costos laborales.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
